/* HEMKIND size guide. Measurements always convert from the source centimetres. */
(() => {
  'use strict';

  const existing = window.HemkindSizeGuide;
  if (existing && typeof existing.refresh === 'function') {
    existing.refresh();
    return;
  }

  const copy = (key, values = {}) => {
    let labels = {};
    try { labels = JSON.parse(document.querySelector('[data-hk-size-labels]')?.textContent || '{}'); } catch (_) {}
    let message = labels[key] || '';
    for (const [token, value] of Object.entries(values)) message = message.replaceAll('__' + token + '__', String(value));
    return message;
  };

  const librarySelector = '[data-hk-size-library]';
  const chartSelector = '[data-hk-chart]';
  const triggerSelector = '[data-hk-size-open]';
  const states = new WeakMap();
  const linkBases = new WeakMap();
  const initializedCharts = new WeakSet();
  let dialog;
  let returnFocus;
  let currentChart;
  let cloneNumber = 0;

  const normalizeCode = (value) => String(value || '').trim().toUpperCase();
  const productIds = (chart) => String(chart.dataset.products || '').split(',').map((value) => value.trim()).filter(Boolean);
  const guideCharts = (library) => Array.from(library.querySelectorAll(chartSelector)).filter((chart) => !chart.closest('[data-hk-size-templates]'));
  const allCharts = () => Array.from(document.querySelectorAll(`${librarySelector} ${chartSelector}`));

  function matchedChart(trigger) {
    const charts = allCharts();
    const productId = String(trigger.dataset.productId || '').trim();
    const manual = productId && charts.find((chart) => productIds(chart).includes(productId));
    if (manual) return manual;

    const code = normalizeCode(trigger.dataset.modelCode);
    if (!code) return null;
    return charts.find((chart) => {
      const library = chart.closest(librarySelector);
      return library && library.dataset.autoMatch === 'true' && normalizeCode(chart.dataset.code) === code;
    }) || null;
  }

  function updateLink(trigger) {
    if (!linkBases.has(trigger)) {
      linkBases.set(trigger, (trigger.getAttribute('href') || '/pages/size-guide').split('#')[0]);
    }
    const base = linkBases.get(trigger);
    const chart = matchedChart(trigger);
    trigger.setAttribute('href', chart && chart.id ? `${base}#${encodeURIComponent(chart.id)}` : base);
    if (chart && typeof HTMLDialogElement !== 'undefined' && typeof HTMLDialogElement.prototype.showModal === 'function') {
      trigger.setAttribute('aria-haspopup', 'dialog');
      if (dialog) trigger.setAttribute('aria-controls', dialog.id);
    } else {
      trigger.removeAttribute('aria-haspopup');
      trigger.removeAttribute('aria-controls');
    }
    return chart;
  }

  function setUnit(scope, requestedUnit) {
    const unit = requestedUnit === 'in' ? 'in' : 'cm';
    scope.dataset.hkCurrentUnit = unit;
    scope.querySelectorAll('[data-cm]').forEach((cell) => {
      const source = cell.getAttribute('data-cm');
      const value = Number(source);
      if (source === null || source.trim() === '' || !Number.isFinite(value)) return;
      cell.textContent = unit === 'in' ? (value / 2.54).toFixed(1) : source;
    });
    scope.querySelectorAll('[data-hk-unit]').forEach((label) => { label.textContent = unit; });
    scope.querySelectorAll('[data-hk-unit-choice]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.hkUnitChoice === unit));
      if (button.tagName === 'BUTTON') button.type = 'button';
    });
  }

  function inLibraryCategory(library, chart) {
    const group = library.dataset.category || 'all';
    const category = chart.dataset.category || '';
    if (group === 'pants') return category === 'pants';
    if (group === 'tops') return ['tees', 'long-sleeves', 'layers'].includes(category);
    return true;
  }

  function optionChart(option, charts) {
    const value = String(option.value || '').replace(/^#/, '');
    return charts.find((chart) => chart.id === value || normalizeCode(chart.dataset.code) === normalizeCode(value));
  }

  function syncSelect(library, charts) {
    library.querySelectorAll('[data-hk-model-select]').forEach((select) => {
      const options = Array.from(select.options);
      options.forEach((option) => {
        if (!option.value) { option.hidden = false; return; }
        const chart = optionChart(option, charts);
        option.hidden = !chart || chart.hidden;
        option.disabled = option.hidden;
      });
      select.querySelectorAll('optgroup').forEach((group) => {
        group.hidden = Array.from(group.querySelectorAll('option')).every((option) => option.hidden);
      });
      const selected = options.find((option) => option.selected);
      if (selected && selected.hidden) {
        const available = options.find((option) => !option.hidden && !option.disabled) || options.find((option) => !option.value);
        select.value = available ? available.value : '';
      }
    });
  }

  function filterLibrary(library, filter) {
    const state = states.get(library) || { filter: 'all' };
    state.filter = ['all', 'tees', 'long-sleeves', 'layers', 'pants'].includes(filter) ? filter : 'all';
    states.set(library, state);
    const charts = guideCharts(library);
    charts.forEach((chart) => {
      chart.hidden = !inLibraryCategory(library, chart) || (state.filter !== 'all' && chart.dataset.category !== state.filter);
      if (chart.hidden) chart.open = false;
    });
    library.querySelectorAll('[data-hk-filter]').forEach((button) => {
      const category = button.dataset.hkFilter;
      const group = library.dataset.category || 'all';
      button.hidden = (group === 'pants' && category !== 'all' && category !== 'pants') || (group === 'tops' && category === 'pants');
      button.setAttribute('aria-pressed', String(category === state.filter));
      if (button.tagName === 'BUTTON') button.type = 'button';
    });
    syncSelect(library, charts);
    const count = charts.filter((chart) => !chart.hidden).length;
    library.querySelectorAll('[data-hk-empty]').forEach((empty) => { empty.hidden = count > 0; });
    library.querySelectorAll('[data-hk-results-count]').forEach((label) => { label.textContent = copy(count === 1 ? 'chart_one' : 'chart_other', { COUNT: count }); });
  }

  function revealChart(chart, shouldFocus) {
    const library = chart.closest(librarySelector);
    if (!library || !inLibraryCategory(library, chart)) return;
    if (chart.hidden) filterLibrary(library, 'all');
    chart.open = true;
    library.querySelectorAll('[data-hk-model-select]').forEach((select) => {
      const option = Array.from(select.options).find((item) => optionChart(item, [chart]));
      if (option) select.value = option.value;
    });
    const summary = chart.querySelector('summary');
    if (shouldFocus && summary) summary.focus({ preventScroll: true });
    chart.scrollIntoView({ block: 'start', behavior: 'auto' });
  }

  function revealHash(shouldFocus) {
    let hash;
    try { hash = decodeURIComponent(window.location.hash.slice(1)); } catch (_) { return; }
    if (!hash.startsWith('fit-')) return;
    const chart = allCharts().find((item) => item.id === hash && !item.closest('[data-hk-size-templates]') && !item.closest(librarySelector).hidden);
    if (chart) revealChart(chart, shouldFocus);
  }

  function uniqueCloneIds(clone) {
    const idMap = new Map();
    const prefix = `hk-fit-dialog-copy-${++cloneNumber}-`;
    const nodes = [clone, ...clone.querySelectorAll('*')];
    nodes.forEach((node) => {
      if (!node.id) return;
      const oldId = node.id;
      node.id = prefix + oldId;
      idMap.set(oldId, node.id);
    });
    nodes.forEach((node) => {
      ['aria-labelledby', 'aria-describedby', 'aria-controls', 'headers'].forEach((attribute) => {
        if (!node.hasAttribute(attribute)) return;
        const value = node.getAttribute(attribute).split(/\s+/).map((id) => idMap.get(id) || id).join(' ');
        node.setAttribute(attribute, value);
      });
      if (idMap.has(node.getAttribute('for'))) node.setAttribute('for', idMap.get(node.getAttribute('for')));
    });
  }

  function ensureDialog() {
    if (dialog) return dialog;
    if (typeof HTMLDialogElement === 'undefined' || typeof HTMLDialogElement.prototype.showModal !== 'function') return null;
    dialog = document.createElement('dialog');
    dialog.id = 'hk-fit-size-dialog';
    dialog.className = 'hk-fit-dialog';
    dialog.setAttribute('aria-labelledby', 'hk-fit-dialog-title');
    dialog.setAttribute('aria-modal', 'true');

    const header = document.createElement('div');
    header.className = 'hk-fit-dialog-header';
    const title = document.createElement('h2');
    title.id = 'hk-fit-dialog-title';
    title.className = 'hk-fit-dialog-title';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'hk-fit-dialog-close';
    close.textContent = copy('close');
    close.setAttribute('aria-label', copy('close_guide'));
    close.autofocus = true;
    close.addEventListener('click', () => dialog.close());
    header.append(title, close);

    const content = document.createElement('div');
    content.className = 'hk-fit-dialog-body';
    content.setAttribute('data-hk-dialog-body', '');
    dialog.append(header, content);
    document.body.appendChild(dialog);

    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const box = dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
    });
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('hk-fit-modal-open');
      const target = returnFocus;
      returnFocus = null;
      currentChart = null;
      if (target && target.isConnected) target.focus({ preventScroll: true });
    });
    return dialog;
  }

  function openChart(trigger, chart) {
    const source = chart.querySelector('[data-hk-chart-content]');
    const modal = ensureDialog();
    if (!modal || !source) return false;
    const library = chart.closest(librarySelector);
    modal.style.cssText = library ? library.style.cssText : '';
    modal.querySelector('.hk-fit-dialog-title').textContent = chart.dataset.title || (chart.querySelector('summary') || chart).textContent.trim();
    const clone = source.cloneNode(true);
    uniqueCloneIds(clone);
    clone.classList.add('hk-fit-chart-content');
    const body = modal.querySelector('[data-hk-dialog-body]');
    body.replaceChildren();
    if (!clone.querySelector('[data-hk-unit-choice]')) {
      const units = document.createElement('div');
      units.className = 'hk-fit-units';
      units.setAttribute('role', 'group');
      units.setAttribute('aria-label', copy('measurement_unit'));
      ['cm', 'in'].forEach((unit) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'hk-fit-unit-button';
        button.setAttribute('data-hk-unit-choice', unit);
        button.textContent = copy(unit === 'cm' ? 'centimetres' : 'inches');
        units.appendChild(button);
      });
      body.appendChild(units);
    }
    body.appendChild(clone);
    setUnit(modal, 'cm');
    returnFocus = trigger;
    currentChart = chart;
    trigger.setAttribute('aria-controls', modal.id);
    if (!modal.open) {
      try { modal.showModal(); } catch (_) { returnFocus = null; currentChart = null; return false; }
    }
    document.documentElement.classList.add('hk-fit-modal-open');
    return true;
  }

  function refresh() {
    document.querySelectorAll(librarySelector).forEach((library) => {
      library.querySelectorAll(chartSelector).forEach((chart) => {
        if (initializedCharts.has(chart)) return;
        initializedCharts.add(chart);
        setUnit(chart, 'cm');
      });
      filterLibrary(library, (states.get(library) || {}).filter || 'all');
    });
    document.querySelectorAll(triggerSelector).forEach(updateLink);
    revealHash(false);
  }

  document.addEventListener('click', (event) => {
    const element = event.target instanceof Element ? event.target : null;
    if (!element) return;
    const trigger = element.closest(triggerSelector);
    if (trigger) {
      const chart = updateLink(trigger);
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || trigger.target === '_blank' || trigger.hasAttribute('download')) return;
      if (chart && openChart(trigger, chart)) event.preventDefault();
      return;
    }
    const unit = element.closest('[data-hk-unit-choice]');
    if (unit) {
      const scope = unit.closest('.hk-fit-dialog') || unit.closest(chartSelector);
      if (scope) { event.preventDefault(); setUnit(scope, unit.dataset.hkUnitChoice); }
      return;
    }
    const filter = element.closest('[data-hk-filter]');
    if (filter) {
      const library = filter.closest(librarySelector);
      if (library) { event.preventDefault(); filterLibrary(library, filter.dataset.hkFilter); }
    }
  });

  document.addEventListener('change', (event) => {
    const select = event.target;
    if (!(select instanceof Element) || !select.matches('[data-hk-model-select]')) return;
    const library = select.closest(librarySelector);
    if (!library) return;
    const option = select.options[select.selectedIndex];
    const chart = option && optionChart(option, guideCharts(library));
    if (chart && !chart.hidden) revealChart(chart, true);
  });

  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', (event) => {
    if (dialog && dialog.open && currentChart && event.target instanceof Element && event.target.contains(currentChart)) dialog.close();
  });
  window.addEventListener('hashchange', () => revealHash(true));
  window.HemkindSizeGuide = { refresh };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();
})();

